import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useFormik } from "formik";
import * as Yup from "yup";

import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import { ButtonBase, FormSectionBox, FormSectionTitle } from "../../styles";
import { PedidosContext } from "../../../../Context/PedidosContext";
import { StyledTableCell } from "../acesso/acessoIndex";
import { ModalCancelamento } from "./modalCancelamento";
import { ModalNegociacao } from "./modalNegociacao";
import { ActionButtons } from "./actionButtons";

import Mensagem from "../../../../Components/Mensagem";
import Loading from "../../../../Components/Loading";
import StatusPedido from "../../../../Components/StatusPedido";
import { ModalContrato } from "./modalContrato";
import api from "../../../../Api/apiAxios";

function toBRDate(value) {
  if (!value) return "**********";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return "**********";
  }
}

function tipoContratoLabel(tipo) {
  if (tipo === 0) return { text: "Manut.", color: undefined };
  if (tipo === 1) return { text: "Locação", color: "darkcyan" };
  if (tipo === 2) return { text: "SaaS", color: "blueviolet" };
  return { text: "-", color: undefined };
}

const getHistoricoId = (h) => Number(h?.id ?? h?.Id ?? 0);
const getHistoricoStatus = (h) => Number(h?.statusContrato ?? h?.StatusContrato ?? 0);
const getHistoricoDataAtualizacao = (h) => new Date(h?.dataAtualizacao ?? h?.DataAtualizacao ?? 0);

const getHistoricoAtivoMaisRecente = (historicoList) => {
  if (!Array.isArray(historicoList) || historicoList.length === 0) return null;

  const ativos = historicoList.filter((h) => getHistoricoStatus(h) === 1);
  if (ativos.length === 0) return null;

  const ordenados = [...ativos].sort((a, b) => getHistoricoDataAtualizacao(b) - getHistoricoDataAtualizacao(a));
  return ordenados[0] ?? null;
};

export function ContratoTab() {
  const navigate = useNavigate();
  const { id } = useParams();
  const pedidoId = useMemo(() => Number(id || 0), [id]);

  const nivelUsuario = window.localStorage.getItem("nivelUsuario");

  const {
    isLoading,
    setIsLoading,
    productVariant,
    mensagemGlobal,
    setMensagemGlobal,
    tipoMensagemGlobal,
    modalCancelamentoContrato,
    modalNegociacao,
    setModalNegociacao,
    negociacoesAnteriores,
    setNegociacoesAnteriores,
    modalContrato,
    setModalContrato,
    fetchContratos,
    verificarECancelarContratos,
    fetchContratoHistorico,
  } = useContext(PedidosContext);

  const validationSchema = Yup.object({
    idContrato: Yup.number().nullable(),
  });

  const formik = useFormik({
    initialValues: { idContrato: 0 },
    validationSchema,
  });

  const [contratoIdPrincipal, setContratoIdPrincipal] = useState(0);
  const [historicoPedido, setHistoricoPedido] = useState([]);
  const [contratoCancelado, setContratoCancelado] = useState(false);

  const [contratoHistoricoIdParaNegociar, setContratoHistoricoIdParaNegociar] = useState(0);

  const existeHistoricoAtivoParaNegociar = useMemo(() => {
    return Array.isArray(historicoPedido) && historicoPedido.some((h) => getHistoricoStatus(h) === 1);
  }, [historicoPedido]);

  const tipoSistemaApi = useMemo(() => {
    return productVariant === "Acesso" ? "Acesso" : productVariant === "Ponto" ? "Ponto" : "Folha";
  }, [productVariant]);

  const getHistoricoIdFromNegociacao = (neg) => Number(neg?.idContrato ?? neg?.IdContrato ?? 0);

  useEffect(() => {
    async function load() {
      if (!pedidoId) return;

      try {
        setIsLoading(true);

        const lista = await fetchContratos(pedidoId);
        const contratos = Array.isArray(lista) ? lista : [];

        if (contratos.length === 0) {
          setContratoIdPrincipal(0);
          setContratoCancelado(false);
          setHistoricoPedido([]);
          setContratoHistoricoIdParaNegociar(0);
          setNegociacoesAnteriores([]);
          return;
        }

        await verificarECancelarContratos();

        const listaAtualizada = await fetchContratos(pedidoId);
        const contratosAtualizados = Array.isArray(listaAtualizada) ? listaAtualizada : [];

        const ativo =
          contratosAtualizados.find((c) => Number(c.statusAtual ?? c.StatusAtual) === 1) ?? null;

        const maisRecente =
          [...contratosAtualizados].sort(
            (a, b) =>
              new Date(b.dataInicio ?? b.DataInicio ?? 0) -
              new Date(a.dataInicio ?? a.DataInicio ?? 0)
          )[0] ?? null;

        const principal = ativo ?? maisRecente;
        const contratoId = Number(principal?.id ?? principal?.Id ?? 0);
        const statusAtual = Number(principal?.statusAtual ?? principal?.StatusAtual);

        setContratoIdPrincipal(contratoId);
        setContratoCancelado(statusAtual === 0);
        formik.setFieldValue("idContrato", contratoId);

        if (contratoId > 0) {
          const histResponse = await fetchContratoHistorico(pedidoId, contratoId);

          const histList = Array.isArray(histResponse)
            ? histResponse
            : histResponse
              ? [histResponse]
              : [];

          const ordenadaDesc = [...histList].sort(
            (a, b) => getHistoricoDataAtualizacao(b) - getHistoricoDataAtualizacao(a)
          );

          setHistoricoPedido(ordenadaDesc);

          const histAtivo = getHistoricoAtivoMaisRecente(ordenadaDesc);
          const histAtivoId = getHistoricoId(histAtivo);

          setContratoHistoricoIdParaNegociar(histAtivoId);

          const ids = ordenadaDesc.map(getHistoricoId).filter((x) => x > 0);

          if (ids.length > 0) {
            const { data } = await api.get(
              `/NegociacaoContrato/historicos?ids=${ids.join(",")}&tipoSistema=${tipoSistemaApi}`
            );

            const ordenadas = Array.isArray(data)
              ? [...data].sort((a, b) => new Date(b.periodoInicial) - new Date(a.periodoInicial))
              : [];

            setNegociacoesAnteriores(ordenadas);
          } else {
            setNegociacoesAnteriores([]);
          }
        } else {
          setHistoricoPedido([]);
          setContratoHistoricoIdParaNegociar(0);
          setNegociacoesAnteriores([]);
        }
      } catch (e) {
        console.error(e);
        setContratoIdPrincipal(0);
        setContratoCancelado(false);
        setHistoricoPedido([]);
        setContratoHistoricoIdParaNegociar(0);
        setNegociacoesAnteriores([]);
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, [pedidoId, productVariant, modalContrato.show, modalCancelamentoContrato.show]);

  useEffect(() => {
    if (!modalNegociacao.show) return;

    const histId = getHistoricoIdFromNegociacao(modalNegociacao.negociacao);
    if (histId > 0) setContratoHistoricoIdParaNegociar(histId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalNegociacao.show]);

  if (isLoading) return <Loading />;

  const hasHistorico = Array.isArray(historicoPedido) && historicoPedido.length > 0;

  const contratoIdAtivoParaAcoes = Number(contratoIdPrincipal || formik.values.idContrato || 0);

  const podeCriarNegociacao =
    nivelUsuario === "1" && existeHistoricoAtivoParaNegociar && contratoHistoricoIdParaNegociar > 0;

  return (
    <>
      {hasHistorico ? (
        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {mensagemGlobal && (
            <Mensagem
              mensagem={mensagemGlobal}
              setMensagem={setMensagemGlobal}
              variant={tipoMensagemGlobal}
            />
          )}

          {modalCancelamentoContrato.show && (
            <ModalCancelamento contratoId={contratoIdAtivoParaAcoes} />
          )}

          {modalNegociacao.show && (
            <ModalNegociacao contratoHistoricoId={contratoHistoricoIdParaNegociar} />
          )}

          {modalContrato.show && (
            <ModalContrato
              contratoHistoricoSelecionado={modalContrato.contrato}
              isShow={modalContrato.isView}
            />
          )}

          <FormSectionBox>
            <FormSectionTitle label="Contrato" />

            <Container sx={{ my: 3 }}>
              {nivelUsuario === "1" && (
                <Box sx={{ display: "flex", width: "98%", justifyContent: "end", pb: 0.5 }}>
                  <Button
                    variant="text"
                    disabled={!contratoCancelado}
                    onClick={() => setModalContrato({ show: true, contrato: {} })}
                    sx={{
                      height: "2.5rem",
                      fontSize: "0.78rem",
                      color: contratoCancelado ? "rgb(19, 35, 80)" : "rgba(0,0,0,0.38)",
                      display: "flex",
                      alignItems: "end",
                    }}
                  >
                    <AddCircleIcon sx={{ marginRight: "0.5rem" }} />
                    Novo Contrato
                  </Button>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ overflow: "visible" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Atualização</StyledTableCell>
                      <StyledTableCell>Cancelamento</StyledTableCell>
                      <StyledTableCell>Valor (R$)</StyledTableCell>
                      <StyledTableCell>Tipo</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Ações</StyledTableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {historicoPedido.map((row, index) => {
                      const tipo = tipoContratoLabel(row.tipoContrato ?? row.TipoContrato);
                      const status = Number(row.statusContrato ?? row.StatusContrato);
                      const valor = Number(row.valor ?? row.Valor ?? 0);

                      return (
                        <TableRow key={row.id ?? row.Id ?? index}>
                          <StyledTableCell>{toBRDate(row.dataAtualizacao ?? row.DataAtualizacao)}</StyledTableCell>
                          <StyledTableCell>{toBRDate(row.dataCancelamento ?? row.DataCancelamento)}</StyledTableCell>
                          <StyledTableCell>{valor.toFixed(2)}</StyledTableCell>
                          <StyledTableCell sx={{ color: tipo.color }}>{tipo.text}</StyledTableCell>
                          <StyledTableCell>
                            <StatusPedido status={status} isPedido isContrato />
                          </StyledTableCell>
                          <StyledTableCell>
                            <ActionButtons item={row} isContrato />
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </FormSectionBox>

          <FormSectionBox>
            <FormSectionTitle label="Negociações" />

            <Container sx={{ my: 3 }}>
              {nivelUsuario === "1" && (
                <Box sx={{ display: "flex", width: "98%", justifyContent: "end", pb: 0.5 }}>
                  <Button
                    variant="text"
                    onClick={() => {
                      if (!podeCriarNegociacao) {
                        setMensagemGlobal("Não é possível incluir negociação: não existe histórico ativo para este contrato.");
                        return;
                      }
                      setModalNegociacao({ show: true, negociacao: {} });
                    }}
                    sx={{
                      height: "2.5rem",
                      fontSize: "0.78rem",
                      color: podeCriarNegociacao ? "rgb(19, 35, 80)" : "rgba(0,0,0,0.38)",
                      display: "flex",
                      alignItems: "end",
                    }}
                    disabled={!podeCriarNegociacao}
                  >
                    <AddCircleIcon sx={{ marginRight: "0.5rem" }} />
                    Nova Negociação
                  </Button>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ overflow: "visible" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Período Inicial</StyledTableCell>
                      <StyledTableCell>Período Final</StyledTableCell>
                      <StyledTableCell>Valor Negociado (R$)</StyledTableCell>
                      <StyledTableCell>Motivo</StyledTableCell>
                      <StyledTableCell>Ações</StyledTableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {negociacoesAnteriores.map((row, index) => (
                      <TableRow key={row.id ?? index}>
                        <StyledTableCell>{toBRDate(row.periodoInicial)}</StyledTableCell>
                        <StyledTableCell>{toBRDate(row.periodoFinal)}</StyledTableCell>
                        <StyledTableCell>{Number(row.valorNegociado ?? 0).toFixed(2)}</StyledTableCell>
                        <StyledTableCell>{row.descricaoMotivo}</StyledTableCell>
                        <StyledTableCell>
                          <ActionButtons item={row} />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </FormSectionBox>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <ButtonBase variant="outlined" onClick={() => navigate("/pedidos")}>
              Voltar
            </ButtonBase>
          </Box>
        </Box>
      ) : (
        <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
          <DoNotDisturbIcon sx={{ color: "gray" }} />
          <Typography variant="h6" sx={{ color: "gray" }}>
            Pedido não possui contrato vinculado no histórico. Solicite a liberação do pedido!
          </Typography>
        </Container>
      )}
    </>
  );
}
